/* oxlint-disable react-hooks/exhaustive-deps -- FIXME: pre-existing errors in hook dependencies */
import {
  Button,
  Field,
  Input,
  Modal,
  ModalProps,
  RichTextarea,
  Select,
  SelectOption,
  SelectProps,
} from '@payaca/ambrosia';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { FieldValidationResult } from '@payaca/types/fieldValidationTypes';
import { IntegratedEmail } from '@payaca/types/integratedEmails';
import { Nullish } from '@payaca/utilities/types';
import { actions as usersActions } from '../../../api/users';
import {
  ReplyToEmailMutationVariables,
  SendEmailMutationVariables,
} from '../../../gql/graphql';

import { ErrorMessage } from '@payaca/components/feedbackMessage/FeedbackMessage';
import MiniLoader from '@payaca/components/miniLoader/MiniLoader';

import ValidatedForm from '@payaca/components/validatedForm/ValidatedForm';

import { useCustomerForDeal } from '@payaca/store/hooks/appState';
import useGetMe from '../../../api/queries/me/useGetMe';

import {
  getArrayMustNotBeEmptyFieldValidator,
  getIsRequiredFieldValidator,
} from '@payaca/helpers/fieldValidationHelper';

import projectKeys from '@/api/queries/project/keyFactory';
import { VALID_EMAIL_REGEX } from '@payaca/constants';
import { useDispatch } from 'react-redux';
import { gqlClient } from '../../../api/graphql-client';
import useGetProjectByIdOverview from '../../../api/queries/project/useGetProjectByIdOverview';
import useGetIntegratedEmailLazy from '../../../api/queries/useGetIntegratedEmail';
import { graphql } from '../../../gql';
import { useAccountUsers } from '@/api/queries/me/useAccountUsers';

const copyOptionGroups = [
  { id: 'customer', label: 'Customer contacts' },
  { id: 'user', label: 'Users' },
  { id: 'other', label: 'Other' },
];

const SendEmailMutation = graphql(`
  mutation SendEmail($email: EmailParamInput!, $entity: EmailEntityInput!) {
    sendEmail(email: $email, entity: $entity)
  }
`);
const ReplyToEmailMutation = graphql(`
  mutation ReplyToEmail(
    $integratedEmailId: Int!
    $email: EmailParamInput!
    $entity: EmailEntityInput!
  ) {
    replyToEmail(
      integratedEmailId: $integratedEmailId
      email: $email
      entity: $entity
    )
  }
`);

export interface SendIntegratedEmailProps {
  projectId: string;
  integratedEmailId?: IntegratedEmail['id']; // determines if reply or new email
}

type SendEmailData = {
  to: string[];
  subject: string;
  message: string;
  cc: string[];
  bcc: string[];
};

interface Props extends Omit<ModalProps, 'title'>, SendIntegratedEmailProps {}
const SendIntegratedEmailModal = (props: Props) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { projectId, integratedEmailId, ...rest } = props;
  const { data: me, refetch } = useGetMe();
  const {
    data: integratedEmailData,
    refetch: fetchIntegratedEmail,
    isFetching,
  } = useGetIntegratedEmailLazy(`${integratedEmailId}`);
  const userIntegrations = useMemo(() => me?.integrations, [me]);
  const { project } = useGetProjectByIdOverview(projectId);
  const customer = useCustomerForDeal(project?.id ? +project.id : undefined);
  const accountUsers = useAccountUsers();

  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [fieldsAddingAdditionalEmail, setFieldsAddingAdditionalEmail] =
    useState<Record<string, boolean>>({});
  const someFieldIsAddingAdditionalEmail = Object.values(
    fieldsAddingAdditionalEmail
  ).some((v) => v);

  const [sendEmailError, setSendEmailError] = useState<false | string>(false);
  const [
    showEmailConnectionInactiveError,
    setShowEmailConnectionInactiveError,
  ] = useState<false | 'connect' | 'reconnect'>(false);

  const isLoading = useMemo(
    () => integratedEmailId && isFetching,
    [integratedEmailId, isFetching]
  );

  const { mutateAsync: mutateSendEmail, isPending: isSendingEmail } =
    useMutation({
      mutationFn: (variables: SendEmailMutationVariables) => {
        return gqlClient.request(SendEmailMutation, variables);
      },
      onSettled: async () => {
        if (project?.publicId) {
          await queryClient.invalidateQueries({
            queryKey: projectKeys.activityFeed(projectId),
          });
        }
      },
    });
  const { mutateAsync: mutateReplyToEmail, isPending: isReplyingToEmail } =
    useMutation({
      mutationFn: (variables: ReplyToEmailMutationVariables) => {
        return gqlClient.request(ReplyToEmailMutation, variables);
      },
      onSettled: async () => {
        if (project?.publicId) {
          await queryClient.invalidateQueries({
            queryKey: projectKeys.activityFeed(projectId),
          });
        }
      },
    });

  const hasEmailSignature = useMemo(() => !!me?.emailSignature, [me]);

  useEffect(() => {
    if (!userIntegrations?.google) {
      setShowEmailConnectionInactiveError('connect');
    } else if (userIntegrations.google.isActive) {
      setShowEmailConnectionInactiveError(false);
    } else {
      setShowEmailConnectionInactiveError('reconnect');
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'userIntegrations.google'
  }, [userIntegrations?.google?.isActive, rest.isOpen]);

  const customerContactOptions = useMemo(() => {
    return (customer?.contacts || [])
      .map((contact): SelectOption<string> | undefined => {
        if (contact.emailAddress) {
          return {
            label: `${contact.name} (${contact.emailAddress})`,
            value: contact.emailAddress,
          };
        }
      })
      .filter((contact) => !!contact);
  }, [customer?.contacts]);

  const copyOptions = useMemo(() => {
    const users = accountUsers
      .filter((u) => me?.user && u.id !== +me?.user.id && !u.deactivatedAt)
      .map((u) => ({ label: u.name, value: u.email, groupId: 'user' }));

    const customerContacts = customerContactOptions.map((c) => ({
      ...c,
      groupId: 'customer',
    }));

    return [...customerContacts, ...users];
  }, [accountUsers, customerContactOptions, me?.user]);

  useEffect(() => {
    if (!rest.isOpen) {
      // closing - reset state
      setShowBcc(false);
      setShowCc(false);
      setSendEmailError(false);
    } else {
      dispatch(usersActions.getAccountUsers());
      if (integratedEmailId) {
        void fetchIntegratedEmail();
      }
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has missing dependencies: 'dispatch', and 'fetchIntegratedEmail'
  }, [rest.isOpen, integratedEmailId]);

  // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React hook useCallback depends on sendEmail which changes every render
  const sendEmail = async (data: SendEmailData) => {
    if (!project) return;
    const email = {
      emailSubject: data.subject,
      emailBody: data.message,
      sendToEmails: data.to,
      sendCcEmails: data.cc,
      sendBccEmails: data.bcc,
    };

    try {
      if (integratedEmailId) {
        await mutateReplyToEmail({
          integratedEmailId,
          email,
          entity: { entityType: 'deal', entityId: project.id },
        });
      } else {
        await mutateSendEmail({
          entity: { entityType: 'deal', entityId: project.id },
          email,
        });
      }
      rest?.onClose?.();
    } catch (err) {
      setSendEmailError('Sorry, there was an error sending your email.');
      // get profile in case userIntegrations has updated with inactive state on email connection
      await refetch();
    }
  };

  const fieldValidators = useMemo(() => {
    return {
      to: [
        getArrayMustNotBeEmptyFieldValidator({
          customErrorMessage: 'You must have at least one recipient',
        }),
      ],
      subject: [getIsRequiredFieldValidator()],
      cc: [],
      bcc: [],
      message: [getIsRequiredFieldValidator()],
    };
  }, []);

  const validEmails = useCallback(
    (emails?: Nullish<string[]>) => {
      // validate emails to ensure they are in the customer contacts
      return (emails || []).filter((em) =>
        customerContactOptions.find((contact) => contact.value === em)
      );
    },
    [customerContactOptions]
  );

  const initialFormState = useMemo(() => {
    const integratedEmail = integratedEmailData?.integratedEmail;
    return {
      to: validEmails(integratedEmail?.from), // replying to the "from" address of the email
      subject: integratedEmail?.subject || '',
      cc: validEmails(integratedEmail?.cc),
      bcc: validEmails(integratedEmail?.bcc) || [],
      message: '',
    };
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useMemo has unnecessary dependency: rest.isOpen
  }, [rest.isOpen, integratedEmailData, validEmails]);

  useEffect(() => {
    if (initialFormState?.cc?.length) {
      setShowCc(true);
    }
    if (initialFormState?.bcc?.length) {
      setShowBcc(true);
    }
  }, [initialFormState]);

  // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React hook useCallback depends on sendEmail which changes every render
  const renderFormContents = useCallback(
    (
      isValid: boolean,
      formState: {
        [key: string]: any;
      },
      validationState: {
        [key: string]: FieldValidationResult;
      },
      touchedState: {
        [key: string]: boolean;
      },
      onFieldChange: (value: { [key: string]: any }) => void
    ) => {
      return (
        <>
          <Modal.Body className="flex flex-col space-y-4">
            <Field.Legacy name="to">
              <div className="flex justify-between">
                <Field.Label>To</Field.Label>
                <div className="space-x-4">
                  {!showCc && (
                    <Button variant="link" onClick={() => setShowCc(true)}>
                      Cc
                    </Button>
                  )}
                  {!showBcc && (
                    <Button variant="link" onClick={() => setShowBcc(true)}>
                      Bcc
                    </Button>
                  )}
                </div>
              </div>
              <MultiSelectEmailWithOther
                options={customerContactOptions}
                value={formState.to}
                onChange={(value) => {
                  onFieldChange({ to: value });
                }}
                onAdditionalEmailBeingAdded={(additionalEmailBeingAdded) => {
                  setFieldsAddingAdditionalEmail((prev) => ({
                    ...prev,
                    to: !!additionalEmailBeingAdded,
                  }));
                }}
              />
            </Field.Legacy>

            {/* cc/bcc */}
            {showCc && (
              <Field.Legacy name="cc">
                <Field.Label>Cc</Field.Label>
                <MultiSelectEmailWithOther
                  options={copyOptions}
                  optionGroups={copyOptionGroups}
                  value={formState.cc}
                  onChange={(value) => onFieldChange({ cc: value })}
                  onAdditionalEmailBeingAdded={(additionalEmailBeingAdded) => {
                    setFieldsAddingAdditionalEmail((prev) => ({
                      ...prev,
                      cc: !!additionalEmailBeingAdded,
                    }));
                  }}
                />
              </Field.Legacy>
            )}
            {showBcc && (
              <Field.Legacy name="bcc">
                <Field.Label>Bcc</Field.Label>
                <MultiSelectEmailWithOther
                  options={copyOptions}
                  optionGroups={copyOptionGroups}
                  value={formState.bcc}
                  onChange={(value) => onFieldChange({ bcc: value })}
                  onAdditionalEmailBeingAdded={(additionalEmailBeingAdded) => {
                    setFieldsAddingAdditionalEmail((prev) => ({
                      ...prev,
                      bcc: !!additionalEmailBeingAdded,
                    }));
                  }}
                />
              </Field.Legacy>
            )}

            <Field.Legacy name="subject">
              <Field.Label>Subject</Field.Label>
              <Input
                value={formState.subject}
                onChange={(value) => onFieldChange({ subject: value })}
              />
            </Field.Legacy>

            <Field.Legacy name="message">
              <Field.Label>Message</Field.Label>
              {hasEmailSignature && (
                <Field.HelperText>
                  Your email signature will be automatically added to the end of
                  your message.
                </Field.HelperText>
              )}
              <RichTextarea
                value={formState.message}
                onChange={(value) => onFieldChange({ message: value })}
                isHtmlOutput
                className="min-h-36"
              />
            </Field.Legacy>
            {sendEmailError && <ErrorMessage message={sendEmailError} />}
            {showEmailConnectionInactiveError && (
              <ErrorMessage
                message={`It looks like you need to ${showEmailConnectionInactiveError} your email account - head to your Profile page to ${showEmailConnectionInactiveError}.`}
              />
            )}
          </Modal.Body>
          <Modal.Footer>
            <Modal.Footer.Actions>
              <Button
                className="mt-2 self-start"
                onClick={() => {
                  void sendEmail(formState as SendEmailData);
                }}
                disabled={
                  someFieldIsAddingAdditionalEmail ||
                  !isValid ||
                  !!showEmailConnectionInactiveError
                }
                isProcessing={isSendingEmail || isReplyingToEmail}
              >
                Send
              </Button>
            </Modal.Footer.Actions>
          </Modal.Footer>
        </>
      );
    },
    [
      showCc,
      showBcc,
      customerContactOptions,
      copyOptions,
      hasEmailSignature,
      sendEmailError,
      showEmailConnectionInactiveError,
      someFieldIsAddingAdditionalEmail,
      isSendingEmail,
      isReplyingToEmail,
      sendEmail,
    ]
  );

  // TODO: replace MiniLoader with a skeleton loader
  return (
    <Modal
      {...rest}
      title={integratedEmailId ? 'Reply to email' : 'Send email'}
      size="md"
      disableBackdropClick
    >
      {isLoading ? (
        <Modal.Body>
          <div className="flex justify-center">
            <MiniLoader />
          </div>
        </Modal.Body>
      ) : (
        <ValidatedForm
          renderFormContents={renderFormContents}
          fieldValidators={fieldValidators}
          initialFormState={initialFormState}
        />
      )}
    </Modal>
  );
};

type AdditionalEmailBeingAddedState = {
  value: string;
  validationError: boolean;
} | null;

const MultiSelectEmailWithOther: FC<
  SelectProps<string, true> & {
    onAdditionalEmailBeingAdded?: (
      state: AdditionalEmailBeingAddedState
    ) => void;
  }
> = ({
  options,
  onChange,
  value,
  onAdditionalEmailBeingAdded,
  ...restProps
}) => {
  const [additionalEmailBeingAdded, setAdditionalEmailBeingAddedState] =
    useState<{
      value: string;
      validationError: boolean;
    } | null>(
      // If no contact emails, show input to add manual email.
      options?.length === 0 ? { value: '', validationError: false } : null
    );

  const setAdditionalEmailBeingAdded = useCallback(
    (state: typeof additionalEmailBeingAdded) => {
      setAdditionalEmailBeingAddedState(state);
      onAdditionalEmailBeingAdded?.(state);
    },
    [onAdditionalEmailBeingAdded]
  );

  const close = useCallback(() => {
    setAdditionalEmailBeingAdded(null);
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useCallback has a missing dependency: 'setAdditionalEmailBeingAdded'
  }, []);

  const addAdditionalEmail = useCallback(() => {
    if (!additionalEmailBeingAdded) return;

    const newEmail = additionalEmailBeingAdded.value.trim();

    const isValid = VALID_EMAIL_REGEX.test(newEmail);

    if (isValid) {
      close();
      const newValue = [...(value ?? []), newEmail];
      onChange?.(newValue, { close });
    } else {
      setAdditionalEmailBeingAdded({
        ...additionalEmailBeingAdded,
        validationError: true,
      });
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useCallback has a missing dependency: 'setAdditionalEmailBeingAdded'
  }, [additionalEmailBeingAdded, onChange, value, close]);

  const optionsWithOthers = useMemo(() => {
    const additionalEmails = (value ?? []).filter(
      (v) => !options.find((o) => o.value === v)
    );

    return [
      ...options,
      ...additionalEmails.map((email) => ({
        label: email,
        value: email,
        groupId: 'other',
      })),
      { label: 'Add other...', value: 'other' },
    ];
  }, [options, value]);

  return (
    <>
      <Select
        {...restProps}
        multiple
        options={optionsWithOthers}
        value={value}
        onChange={(values, actions) => {
          if (values.includes('other')) {
            actions.close();
            setAdditionalEmailBeingAdded({ value: '', validationError: false });
          } else {
            setAdditionalEmailBeingAdded(null);
          }
          onChange?.(
            values.filter((v) => v !== 'other'),
            actions
          );
        }}
      />
      {additionalEmailBeingAdded !== null && (
        <div className="mt-2 flex items-start">
          <Input
            className="flex-grow"
            placeholder="Enter email address"
            autoFocus={true}
            onChange={(value) =>
              setAdditionalEmailBeingAdded({ value, validationError: false })
            }
            value={additionalEmailBeingAdded?.value ?? ''}
            onKeyUp={(e) => {
              if (e.key === 'Enter') {
                addAdditionalEmail();
              }
            }}
          />

          <Button className="ml-2" variant="secondary" onClick={close}>
            Cancel
          </Button>
          <Button className="ml-2" onClick={addAdditionalEmail}>
            Add
          </Button>
        </div>
      )}
      {additionalEmailBeingAdded?.validationError && (
        <div className="text-red-500 mt-2">
          Please enter a valid email address (e.g., user@example.com)
        </div>
      )}
    </>
  );
};

export default SendIntegratedEmailModal;
